import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StoreContext } from '../../../models/store/context';
import { QuoteStatus } from '../../../models/store/quoteStatus';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { saveSession } from '../../../services/session/save';

/**
 * Stores quote response data and computed offer details for quote/confirmation views.
 *
 * Chooses payment method preference for display values, navigates to quote,
 * and persists the updated context to session storage.
 *
 * @param context Current store context.
 * @param event Event containing quote response data.
 * @param enqueue Store enqueue helpers used for navigation emission.
 * @returns Updated context with quote, confirmation offer data, and GA fields.
 */
export const setQuote = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: QuoteResponse },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  const quote = event.data;
  const subscription = quote?.offer?.contracts[0]?.contractAttributes.subscription;

  const paymentMethod = subscription?.cc?.discountProfileAttributes
    ? 'cc'
    : subscription?.ach?.discountProfileAttributes
      ? 'ach'
      : 'cc';

  const offerData = {
    subscription,
    startDate: quote?.offer.contracts[0]?.contractAttributes.effectiveDate?.split('T')[0],
    netFirstPaymentAmount: subscription?.[paymentMethod]?.netFirstPaymentAmount,
    netFirstPaymentAmountAfterPromotionalPeriod:
      subscription?.[paymentMethod]?.discountProfileAttributes
        ?.netFirstPaymentAmountAfterPromotionalPeriod,
    discountCode: subscription
      ? [
          subscription.cc?.discountProfileAttributes?.ccDiscountCodeApplied,
          subscription.ach?.discountProfileAttributes?.achDiscountCodeApplied,
        ]
          .filter((code): code is string => !!code && code.toString().trim().length > 0)
          .join(' | ')
      : '',
  };

  enqueue.emit.navigate({ path: AppRoute.QUOTE });

  const updatedContext = {
    ...context,
    quote: {
      status: QuoteStatus.READY,
      data: quote,
    },
    confirmation: {
      ...context.confirmation,
      applianceDetails: {
        ...context.confirmation?.applianceDetails,
        guarantee: quote?.items?.[0]?.guarantee ?? 12, // default to 12 months if guarantee length is not available from quote response
      },
      offerData,
    },
    gaEventData: {
      ...context.gaEventData,
      netFirstPaymentAmount: subscription?.[paymentMethod]?.netFirstPaymentAmount,
    },
  };

  saveSession(updatedContext);

  return updatedContext;
};
